/**
 * Types for the cross-product Audit Logs API (`/api/v2/audit-logs`).
 *
 * The backend resolves the tenant from the Auth0 token; no `tenantId`
 * goes in the request payloads.
 */

export type AuditLogAction =
  | "create"
  | "update"
  | "delete"
  | "associate"
  | "dissociate"
  | "bulk_update"
  | "claim";

export type AuditLogSource = "dashboard" | "pipeline" | "admin" | "system";

/**
 * Resource type identifier as returned by the backend.
 *
 * NOT a closed enum: the runtime list comes from
 * `GET /api/v2/audit-logs/filters/options` and is product-scoped per
 * tenant (a customer with only Reflector V2 sees a different subset
 * than one with Kontrol Edge). The UI MUST populate dropdowns from the
 * options endpoint, never from a hardcoded list.
 *
 * Kept as `string` (instead of a literal union) so we don't accidentally
 * grow a static enum in this file as the backend adds new resource
 * types.
 */
export type AuditLogResourceType = string;

/**
 * Generic JSON payload for `before` / `after` snapshots and `metadata`.
 * The backend stores arbitrary keys per resource type so we can't narrow
 * further without losing fidelity.
 */
export type AuditLogJsonValue =
  | string
  | number
  | boolean
  | null
  | AuditLogJsonValue[]
  | { [key: string]: AuditLogJsonValue };

export interface AuditLogChanges {
  before?: Record<string, AuditLogJsonValue> | null;
  after?: Record<string, AuditLogJsonValue> | null;
}

export interface AuditLogProductRef {
  id: number;
  name: string;
}

export interface AuditLog {
  id: number;
  tenantId: number;
  productId: number;
  /** Auth0 subject of the actor (`samlp|CTX-SSO|someone@example.com`), or null. */
  userId: string | null;
  userEmail: string;
  action: AuditLogAction;
  resourceType: AuditLogResourceType | string;
  resourceId: number;
  resourceName: string | null;
  parentType: AuditLogResourceType | string | null;
  parentId: number | null;
  changes: AuditLogChanges | null;
  metadata: Record<string, AuditLogJsonValue> | null;
  source: AuditLogSource;
  /** ISO 8601 string. */
  createdAt: string;
  product: AuditLogProductRef;
}

/** Query params for `GET /api/v2/audit-logs`. All optional. */
export interface AuditLogsQueryParams {
  /**
   * Tenant scope, derived from the global header selection (NOT from the
   * URL — see `MainAuditLogs`). When set:
   *   - tenantId === own partner id → backend returns the partner aggregate
   *     (all customers of that partner).
   *   - tenantId === a customer's id → backend filters to that customer
   *     only.
   * Backend MUST validate that the requesting JWT owns this tenantId
   * (same tenant or partner-of-it) and return 403 otherwise.
   */
  tenantId?: number;
  productId?: number;
  resourceType?: AuditLogResourceType | string;
  resourceId?: number;
  parentType?: AuditLogResourceType | string;
  parentId?: number;
  /** Single value or CSV list (e.g. "create,update"). */
  action?: AuditLogAction | AuditLogAction[];
  source?: AuditLogSource;
  /** Exact match on the Auth0 subject. */
  userId?: string;
  /** LIKE %term% on backend. */
  userEmail?: string;
  /** ISO date — lower bound of `createdAt`. */
  from?: string;
  /** ISO date — upper bound of `createdAt`. */
  to?: string;
  /** LIKE on `resourceName` OR `userEmail`. */
  search?: string;
  page?: number;
  /** Default 25, max 100. */
  limit?: number;
}

export interface AuditLogsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** Generic envelope used by every endpoint in this module. */
export interface AuditLogsApiEnvelope<TData> {
  success: boolean;
  message: string;
  data: TData;
}

export type AuditLogsListResponse = AuditLogsApiEnvelope<AuditLog[]> & {
  pagination: AuditLogsPagination;
};

export type AuditLogDetailResponse = AuditLogsApiEnvelope<AuditLog>;

export interface AuditLogsFilterOptions {
  products: AuditLogProductRef[];
  actions: AuditLogAction[];
  resourceTypes: (AuditLogResourceType | string)[];
  sources: AuditLogSource[];
}

export type AuditLogsFilterOptionsResponse =
  AuditLogsApiEnvelope<AuditLogsFilterOptions>;

/** Page-size options exposed in the table footer. */
export const AUDIT_LOGS_PAGE_SIZE_OPTIONS: number[] = [25, 50, 100];
export const AUDIT_LOGS_DEFAULT_PAGE_SIZE = 25;
